import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { APIService } from "@utils/apiService";
import { AppConfig } from "@config/config";
import { ActivePromotionCycleInterface, PromotionRequest } from "@utils/types";

export interface FunctionalLeadTimeBasedState {
  state: "failed" | "success" | "loading" | "idle";
  requestState: "failed" | "success" | "loading" | "idle";
  stateMessage: string | null;
  activePromotionCycle: ActivePromotionCycleInterface | null;
  promotionRequests: PromotionRequest[];
  errorMessage: string | null;
}

const initialState: FunctionalLeadTimeBasedState = {
  state: "idle",
  requestState: "loading",
  stateMessage: null,
  errorMessage: null,
  activePromotionCycle: null,
  promotionRequests: []
};

export const getActivePromotionCycle = createAsyncThunk(
  "timebased/getActivePromotionCycle",
  async (_, { dispatch }) => {
    return new Promise<{
      promotionCycle: ActivePromotionCycleInterface | null;
    }>((resolve, reject) => {
      Promise.all([
        APIService.getInstance().get<{
          promotionCycles: ActivePromotionCycleInterface[];
        }>(AppConfig.serviceUrls.getActivePromotionCycle),
      ])
        .then(([open]) => {
          if (
            open.data.promotionCycles &&
            open.data.promotionCycles.length > 0
          ) {
            dispatch(getAllTimebasedPromotionRequests(open.data.promotionCycles[0].id))
            resolve({
              promotionCycle: open.data.promotionCycles[0],
            });
          } else {
            resolve({
              promotionCycle: null,
            });
          }
        })
        .catch((error: Error) => {
          reject(error);
        });
    });
  }
);

export const getAllTimebasedPromotionRequests = createAsyncThunk(
  "timebased/getAllTimebasedPromotionRequests",
  async (cycleId: number, { dispatch }) => {
    return new Promise<{
      promotionRequests: PromotionRequest[];
    }>((resolve, reject) => {
      Promise.all([
        APIService.getInstance().get<{
          promotionRequests: PromotionRequest[];
        }>(
            AppConfig.serviceUrls.retrieveFLAllPromotionApplications +
            "&type=TIME_BASED&cycleId=" +
            cycleId
        ),
      ])
        .then(([reqs]) => {
            resolve({
                promotionRequests: reqs.data.promotionRequests,
            });
        })
        .catch((error: Error) => {
          reject(error);
        });
    });
  }
);

export const FunctionalLeadTimeBased = createSlice({
  name: "functional-lead-timeBased-promotion",
  initialState,
  reducers: {
    reset: (state) => {
      state.state = "idle";
      state.requestState = "idle";
      state.stateMessage = "";
      state.activePromotionCycle = null;
      state.promotionRequests = [];
    },
    setStateMessage: (state, action: PayloadAction<string>) => {
      state.stateMessage = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getActivePromotionCycle.pending, (state, action) => {
        state.stateMessage = "Loading Promotion Requests";
        state.state = "loading";
      })
      .addCase(getActivePromotionCycle.fulfilled, (state, action) => {
        state.activePromotionCycle = action.payload.promotionCycle;
        state.state = "success";
      })
      .addCase(getActivePromotionCycle.rejected, (state, action) => {
        state.state = "failed";
      })

      .addCase(getAllTimebasedPromotionRequests.pending, (state, action) => {
        state.stateMessage = "Loading Promotion Requests";
        state.requestState = "loading";
      })
      .addCase(getAllTimebasedPromotionRequests.fulfilled, (state, action) => {
        state.promotionRequests = action.payload.promotionRequests;
        state.requestState = "success";
      })
      .addCase(getAllTimebasedPromotionRequests.rejected, (state, action) => {
        state.requestState = "failed";
      })
  },
});

export const { reset, setStateMessage } = FunctionalLeadTimeBased.actions;

export default FunctionalLeadTimeBased.reducer;
